<template>
  <v-dialog
    v-model="getImageViewer.show"
    width="90vw"
    max-width="1600"
    persistent
    content-class="clerk-office-dialog"
  >
    <v-card class="clerk-office-card rounded-xl">
      <v-card-title class="clerk-office-header pa-0">
        <div class="header-content">
          <div class="header-icon">
            <v-icon color="white" large>
              mdi-bank
            </v-icon>
          </div>

          <div>
            <div class="text-h4 font-weight-bold">
              Circuit Clerk's Office
            </div>
          </div>

          <v-spacer />

          <div class="close-section">
            <span class="close-label">Closing shortly</span>

            <v-progress-circular
              :rotate="180"
              :size="52"
              :width="6"
              :value="closePercentage"
              color="error"
              class="close-progress"
              @click="closeImageViewer"
            >
              <v-icon color="error">
                mdi-close
              </v-icon>
            </v-progress-circular>
          </div>
        </div>
      </v-card-title>

      <v-divider />

      <v-card-text class="pa-5 image-content">
        <v-row class="image-row" dense>
          <!-- Route Map -->
          <v-col cols="12" md="7">
            <v-card outlined class="image-card route-card">
              <div class="image-card-header">
                <div class="step-number">1</div>

                <div>
                  <div class="font-weight-bold text-h6">
                    Follow the Blue Route
                  </div>

                  <div class="text-body-2">
                    Walk toward the Circuit Clerk's Office entrance.
                  </div>
                </div>
              </div>

              <div class="image-container">
                <v-img
                  :src="routeImage"
                  contain
                  class="direction-image"
                  alt="Route to Circuit Clerk's Office"
                />
              </div>

              <div class="image-card-footer">
                <v-icon color="primary" class="mr-2">
                  mdi-arrow-right-bold-circle
                </v-icon>

                Follow the blue line to the entrance.
              </div>
            </v-card>
          </v-col>

          <!-- Entrance Photo -->
          <v-col cols="12" md="5">
            <v-card outlined class="image-card entrance-card">
              <div class="image-card-header">
                <div class="step-number">2</div>

                <div>
                  <div class="font-weight-bold text-h6">
                    Look for This Entrance
                  </div>

                  <div class="text-body-2">
                    The sign reads “Circuit Clerk / Traffic Fines.”
                  </div>
                </div>
              </div>

              <div class="image-container">
                <v-img
                  :src="entranceImage"
                  contain
                  class="direction-image"
                  alt="Circuit Clerk Traffic Fines entrance"
                />
              </div>

              <div class="image-card-footer">
                <v-icon color="success" class="mr-2">
                  mdi-check-circle
                </v-icon>

                Enter through this door.
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider />

      <v-card-actions class="px-6 py-4 clerk-office-footer">
        <v-icon color="success" class="mr-2">
          mdi-check-circle
        </v-icon>

        <span class="font-weight-medium">
          Follow the map, then enter through the Circuit Clerk / Traffic Fines door.
        </span>

        <v-spacer />

        <v-btn
          color="primary"
          large
          depressed
          class="px-8"
          @click="closeImageViewer"
        >
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from "vuex";

import routeImage from "@/assets/clerk-route.png";
import entranceImage from "@/assets/entranceImage.png";

const AUTO_CLOSE_MS = 20000;
const PROGRESS_INTERVAL_MS = 250;

export default {
  name: "ImageViewer",

  data() {
    return {
      routeImage,
      entranceImage,
      closePercentage: 0,
      timeout: null,
      intervalObj: null,
    };
  },

  computed: {
    ...mapGetters(["getImageViewer", "getIsSpeaking"]),
  },

  watch: {
    "getImageViewer.show": {
      immediate: true,
      handler(isOpen) {
        if (!isOpen) {
          this.stopAutoClose();
          return;
        }

        this.$nextTick(() => {
          this.startAutoClose();
        });
      },
    },

    getIsSpeaking(isSpeaking) {
      if (!this.getImageViewer.show) {
        return;
      }

      if (isSpeaking) {
        this.stopAutoClose();
      } else {
        this.startAutoClose();
      }
    },
  },

  methods: {
    clearTimers() {
      if (this.timeout) {
        clearTimeout(this.timeout);
        this.timeout = null;
      }

      if (this.intervalObj) {
        clearInterval(this.intervalObj);
        this.intervalObj = null;
      }
    },

    startAutoClose() {
      this.clearTimers();

      if (!this.getImageViewer.show || this.getIsSpeaking) {
        return;
      }

      this.closePercentage = 0;

      const progressIncrease =
        (100 * PROGRESS_INTERVAL_MS) / AUTO_CLOSE_MS;

      this.intervalObj = setInterval(() => {
        this.closePercentage = Math.min(
          100,
          this.closePercentage + progressIncrease
        );
      }, PROGRESS_INTERVAL_MS);

      this.timeout = setTimeout(() => {
        this.closeImageViewer();
      }, AUTO_CLOSE_MS);
    },

    stopAutoClose() {
      this.clearTimers();
      this.closePercentage = 0;
    },

    closeImageViewer() {
      this.stopAutoClose();
      this.$store.commit("closeImageViewer");
    },
  },

  beforeDestroy() {
    this.clearTimers();
  },
};
</script>

<style scoped>
.clerk-office-card {
  overflow: hidden;
  background: #f4f7fb;
}

.clerk-office-header {
  background: linear-gradient(135deg, #0a2650 0%, #174c90 100%);
  color: white;
}

.header-content {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 95px;
  padding: 18px 28px;
}

.header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 58px;
  height: 58px;
  margin-right: 18px;
  border: 2px solid rgba(255, 255, 255, 0.8);
  border-radius: 50%;
}

.header-subtitle {
  margin-top: 2px;
  color: rgba(255, 255, 255, 0.85);
}

.close-section {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.close-label {
  margin-bottom: 4px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 11px;
}

.close-progress {
  cursor: pointer;
}

.image-content {
  background: #f4f7fb;
}

.instruction-banner {
  display: flex;
  align-items: center;
  max-width: 1500px;
  margin: 0 auto 18px;
  padding: 14px 20px;
  border-left: 5px solid #1565c0;
  border-radius: 9px;
  background: white;
  box-shadow: 0 2px 9px rgba(0, 0, 0, 0.1);
}

.image-row {
  max-width: 1500px;
  margin: 0 auto;
}

.image-card {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  border-radius: 12px;
  background: white;
}

.image-card-header {
  display: flex;
  align-items: center;
  min-height: 84px;
  padding: 14px 18px;
  border-bottom: 1px solid #e4e8ef;
  background: #ffffff;
}

.step-number {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 42px;
  height: 42px;
  margin-right: 14px;
  border-radius: 50%;
  background: #1565c0;
  color: white;
  font-size: 22px;
  font-weight: 800;
}

.image-container {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  min-height: 0;
  padding: 10px;
  background: #edf2f7;
}

.direction-image {
  width: 100%;
  height: 47vh;
  min-height: 330px;
  max-height: 620px;
  border-radius: 8px;
  background: white;
}

.image-card-footer {
  display: flex;
  align-items: center;
  min-height: 58px;
  padding: 14px 18px;
  color: #173a69;
  font-size: 17px;
  font-weight: 600;
}

.clerk-office-footer {
  min-height: 70px;
  background: white;
  color: #173a69;
  font-size: 16px;
}

@media (max-width: 960px) {
  .header-content {
    min-height: 78px;
    padding: 14px 18px;
  }

  .header-icon {
    width: 46px;
    height: 46px;
    margin-right: 12px;
  }

  .image-card-header {
    min-height: 72px;
  }

  .direction-image {
    height: 38vh;
    min-height: 280px;
  }

  .instruction-banner {
    padding: 12px 14px;
  }
}
</style>